import {EventEmitter, Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {Amenity, Building, Parcel, ParkingPass, Resident, UserAddressType, UserPhoneType} from '../types/data.types';
import {UrlService} from './url.service';
import {AccountPermissionsService} from './account-permissions.service';
import {AuthenticationService} from './authentication.service';
import {AmenitiesService} from './amenities.service';
import {ParkingPassService} from './parking-pass.service';
import {Subject} from 'rxjs/Subject';
import {IResidentPatch} from '../types/api-response/building/resident-patch';
import {BuildingApiService} from './api/building-api.service';
import {startWith, tap} from 'rxjs/operators';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';

declare const toastr: any;
declare const moment: any;
declare const swal: any;

@Injectable()
export class DirectoryService {
    onDataReceived: EventEmitter<any> = new EventEmitter();
    onDataSend: EventEmitter<any> = new EventEmitter();
    onDataUpdated: EventEmitter<any> = new EventEmitter();
    onUnitChangeCompleted: EventEmitter<any> = new EventEmitter();
    residentInstructionUpdated: EventEmitter<any> = new EventEmitter();
    spinnerShow = false;

    currentTabId = 'residents';
    previousUrl = '/directory';
    countries: Array<any> = [];
    buildings: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    buildingsForRead: Array<any> = [];
    groups: { id: number, itemName: string }[] = [];
    petTypes: Array<any> = [];
    quickSearchStore$: Subject<any> = new Subject();
    quickSearchStore: any;
    onToggleDetailedView: any = new EventEmitter();
    showDetailedView = false;
    showServiceUnavailable = false;
    selectedItemData: any;
    showCreateModal = false;
    showMoveOutModal = false;
    showAddInstructionsModal = false;
    showSendInvitationModal = false;
    showResetPasswordModal = false;
    showImageCaptureModal = false;
    isShowChangeUnitModal = false;
    sendInvitationMessageData: any;
    selectedBuilding: number;
    serviceName: string = 'bldn';
    moveOutFormResident: any;
    changeUnitModalObject: any;
    instructionsModalType = '';
    instructionsModalUser = null;
    instructionsObservable = null;
    instructionInInstructionsModal = null;
    selectedResidentInstructions = [];
    vendorsCategories = [];
    vendorsPaymentMethods = [];
    residentOccupancyTypes = [];
    occupancyTypesLoading = false;
    vendorsCategoriesReceived: EventEmitter<any> = new EventEmitter();
    imageCaptured: EventEmitter<any> = new EventEmitter();

    userUpdateModalData: IResidentPatch = null;
    loadingCorporations = false;
    corporations = [];

    currentCorporationSettings = {};
    corporationSettingsSubscribtion = null;

    currentCorporationSmsSettings = null;
    corporationSettingsSmsSubscribtion = null;
    smsMessageResidentData: any;
    showSmsModal = false;

    directoryData = {
        'updates': {
            get: '/v1/resident_patch.json?processedAt=is_null_value&expand=suite.building,suiteUser.user.groups',
            post: '/v1/resident_patch.json',
            patch: '/v1/resident_patch/',
            remove: '/v1/resident_patch/',
            data: [],
            total: null
        },
        'residents': {
            get: '/v1/suite-users.json?expand=user.phones,user.addresses,user.groups.group,suite,building,building.timeZone,user.consent,user.buzzFields,user.keyCodeFields,user.keyFobFields,suite.buzzFields,suite.keyCodeFields,suite.keyFobFields,user.vehicles,suite.pet,suite.pet.petType',
            post: '/v1/suite-users.json',
            patch: '/v1/suite-users/',
            remove: '/v1/suite-users/',
            data: [],
            total: null
        },
        'units': {
            get: '/v1/suites.json?expand=suiteUser.user,building,buzzFields,keyCodeFields,keyFobFields,user',
            post: '/v1/suites.json',
            patch: '/v1/suites/',
            remove: '/v1/suites/',
            data: [],
            total: null
        },
        'parking-spots': {
            get: '/v1/parking.json?expand=suite,building',
            post: '/v1/parking.json',
            patch: '/v1/parking/',
            remove: '/v1/parking/',
            data: [],
            total: null
        },
        'lockers': {
            get: '/v1/locker.json?expand=suite,building',
            post: '/v1/locker.json',
            patch: '/v1/locker/',
            remove: '/v1/locker/',
            data: [],
            total: null
        },
        'vehicles': {
            get: '/v1/vehicle.json?expand=owner.suiteUser,suite,building',
            post: '/v1/vehicle.json',
            patch: '/v1/vehicle/',
            remove: '/v1/vehicle/',
            data: [],
            total: null
        },
        'pets': {
            get: '/v1/pet.json?expand=suite,building,petType',
            post: '/v1/pet.json',
            patch: '/v1/pet/',
            remove: '/v1/pet/',
            data: [],
            total: null
        },
        'bike-racks': {
            get: '/v1/bike_rack.json?expand=suite,building,rentedTo',
            post: '/v1/bike_rack.json',
            patch: '/v1/bike_rack/',
            remove: '/v1/bike_rack/',
            data: [],
            total: null
        },
        'vendors': {
            get: '/v1/vendors?',
            post: '/v1/vendors',
            patch: '/v1/vendors/',
            remove: '/v1/vendors/',
            data: [],
            total: null
        }
    };

    constructor(
        public api: ApiClientService,
        public buildingApi: BuildingApiService,
        public urlService: UrlService,
        public account: AccountPermissionsService,
        public authenticationService: AuthenticationService,
        public amenitiesService: AmenitiesService,
        public parkingPassService: ParkingPassService) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    getData(scrollToTop?) {
        this.getDataByQueryParams(scrollToTop).subscribe();
    }

    getDataByQueryParams(scrollToTop?) {
        this.showServiceUnavailable = false;
        const tabId = this.urlService.queryParams['tab'];
        const url = this.directoryData[tabId].get;
        this.onDataSend.emit();
        const queryParams = this.urlService.getQueryParamsString();
        return this.api.getJson(tabId != 'vendors' ? 'domainBD' : 'domainVendors', `${url}${queryParams}`)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity((item) => {
                    if (item.building && item.building.bdId) item.building.id = item.building.bdId;
                    return this.account.addPermissionsToEntity(item, 'bldn');
                }),
                tap(({loading}) => this.spinnerShow = loading),
                tap(
                    (responseBody) => {
                        this.directoryData[tabId].data = responseBody.entities;
                        this.directoryData[tabId].total = responseBody.total;
                        this.showDetailedView = false;
                        this.onDataReceived.emit({id: tabId, data: responseBody, scrollToTop: scrollToTop});
                    },
                    () => {
                        this.showServiceUnavailable = true;
                        this.spinnerShow = false;
                    }
                ),
            );
    }

    toggleDetailedView(data, state: boolean): void {
        this.selectedItemData = data;
        if (this.selectedItemData && this.selectedItemData.alternativeEmails && this.selectedItemData.alternativeEmails.length) {
            this.getAlternativeEmails(this.selectedItemData.suiteUserId, this.selectedItemData.id);
        }
        this.showDetailedView = state;
        this.onToggleDetailedView.emit(data);
    }

    getAlternativeEmails(suiteUserId, id) {
        const tabId = this.urlService.queryParams['tab'];
        const url = `/v1/suite-users/${suiteUserId}.json?expand=user.alternativeEmails`;

        this.api.getJson(tabId != 'vendors' ? 'domainBD' : 'domainVendors', `${url}`)
            .subscribe(
                (res) => {
                    const data = this.directoryData.residents.data.find(item => item.id == suiteUserId);
                    data.user.alternativeEmails = res.user.alternativeEmails;

                    if (this.selectedItemData.id == id) {
                        this.selectedItemData.alternativeEmails = res.user.alternativeEmails;
                    }
                },
                (error) => {
                    console.warn('DirectoryService -> getAlternativeEmails -> error', error);
                }
            );
    }

    resetDirectoryData() {
        for (const item in this.directoryData) {
            this.directoryData[item].data = [];
            this.directoryData[item].total = null;
        }
    }

    getBuildings(): void {
        if (this.buildings.length) return;

        const url = `/v1/buildings/short.json?per-page=10000&order-by=name|asc&expand=timeZone`;
        this.api.get('domainBD', url).subscribe(
            (res) => {
                this.buildings = [];
                const entities = JSON.parse(res._body).entities || [];
                entities.forEach((item) => {
                    const building = new Building(item.id, item.name, item.suits);
                    (<any>building).corporationNumber = item.corporationNumber;
                    (<any>building).corporationNumberName = item.corporationNumberName;
                    (<any>building).address = item.address;
                    (<any>building).timezone = item.timeZone ? item.timeZone.value : '';
                    this.buildings.push(building);
                });
                if (this.buildings.length === 1) this.selectedBuilding = this.buildings[0].id;
                this.getFilteredBuildings();
            },
            (error) => this.api.errorHandler(error, 'Failed get buildings'));
    }

    getCountries(): void {
        if (this.countries.length) return;
        const url = `/v1/country.json?per-page=1000&order-by=countryName|asc`;
        this.api.get('domainBD', url).subscribe(
            (res) => {
                this.countries = [];
                const entities = JSON.parse(res._body).entities || [];
                this.countries = entities.map(item => ({id: item.id, countryName: item.countryName}));
                // entities.forEach((item) => {
                //     this.countries.push({ id: item.id, countryName: item.countryName });
                // });
            },
            (error) => this.api.errorHandler(error, 'Failed get countries'));
    }

    getGroups(): void {
        if (this.groups.length) {
            return;
        }

        this.buildingApi.getAllUserGroups().subscribe(result => {
                this.groups = result.entities.map(item => ({id: item.id, itemName: item.name}));
            },
            (error) => this.api.errorHandler(error, 'Failed get user groups'));
    }

    getPetTypes(): void {
        if (this.petTypes.length) return;
        const url = '/v1/pet_type.json?per-page=1000&order-by=name|asc';
        this.api.get('domainBD', url).subscribe((res) => {
            this.petTypes = JSON.parse(res._body).entities;
        });
    }

    getBuildingNameById(id): string {
        for (let i = 0; i < this.buildings.length; i++) {
            if (this.buildings[i].id === id) return this.buildings[i].name;
        }
        return '';
    }

    getPetTypeNameById(id): string {
        for (let i = 0; i < this.petTypes.length; i++) {
            if (this.petTypes[i].id == id) return this.petTypes[i].name;
        }
        return '';
    }

    getUrl(type: string) {
        return this.directoryData[this.currentTabId][type];
    }

    hideModal() {
        this.showCreateModal = false;
    }

    showModal() {
        this.showCreateModal = true;
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    sendInvitationToTheResident(resident: Resident, corpNumber, isInvitation) {
        const recipients = [{
            name: resident.fullName,
            email: resident.email,
            alternativeEmails: resident.alternativeEmails,
            type: resident.role,
            buildingId: resident.buildingId,
            buildingName: resident.buildingName,
            buildingAddress: resident.buildingAddress,
            suiteId: resident.suiteId,
            suiteName: resident.suiteNumber,
            bdId: resident.id,
            homePhone: resident.phones.find(p => p.phoneType == 'phone_home') ? resident.phones.find(p => p.phoneType == 'phone_home').phone : undefined,
            mobilePhone: resident.phones.find(p => p.phoneType == 'phone_cell') ? resident.phones.find(p => p.phoneType == 'phone_cell').phone : undefined,
            businessPhone: resident.phones.find(p => p.phoneType == 'phone_business') ? resident.phones.find(p => p.phoneType == 'phone_business').phone : undefined,
            suiteCommonElementsFee: resident.commonElementsFee,
        }];

        const filters = [resident.getEmailFilters()];

        const data: any = {
            sender: 'notice@maxcondoclub.ca',
            senderName: 'MaxCondoClub',
            subject: isInvitation ? 'Invitation' : '',
            message: '',
            sendMeCopy: 0,
            filters: JSON.stringify(filters),
            recipients: recipients,
            attachments: [],
            pmEmail: this.authenticationService.user.email,
            pmMobilePhone: this.authenticationService.user.primaryPhone,
            pmName: this.authenticationService.user.fullName,
            buildingId: [resident.buildingId],
            buildingName: resident.buildingName,
            buildingAddress: resident.buildingAddress,
            status: 'in_progress',
            corpNumber: corpNumber,
            isInvitation: isInvitation
        };

        if (isInvitation) {
            this.api.get('domainBD', `/v1/corporation_number/${corpNumber}.json`).subscribe(resp => {
                const res = JSON.parse(resp._body);

                data.inviteTemplate = res.inviteTemplate ? res.inviteTemplate : 'Invitation';
                data.subject = res.inviteSubject ? res.inviteSubject : '';

                this.sendInvitationMessageData = data;
                this.showSendInvitationModal = true;
            }, error => {
                // this.api.errorHandler(error, 'Failed to get corporation information.');
                this.sendInvitationMessageData = data;
                this.showSendInvitationModal = true;
            });
        } else {
            data.inviteTemplate = '';
            this.sendInvitationMessageData = data;
            this.showSendInvitationModal = true;
        }
    }

    showChangeUnitModal(data) {
        this.isShowChangeUnitModal = true;
        this.changeUnitModalObject = data;
    }

    residentFactory(entities): Resident[] {

        let residents = entities.map((suiteUser) => {
            if (!suiteUser.user) return;
            const resident = suiteUser.user;
            const phonesArr: Array<UserPhoneType> = [];
            const addressesArr: Array<UserAddressType> = [];
            const groups = [];

            resident.phones.forEach((phone) => {
                phonesArr.push(new UserPhoneType(
                    phone.isPrimaryPhone,
                    phone.phone,
                    phone.phoneType,
                    phone.user,
                    phone.realPhoneType
                ));
            });

            resident.addresses.forEach((address) => {
                addressesArr.push(new UserAddressType(
                    address.isPrimaryAddress,
                    address.country,
                    address.state,
                    address.city,
                    address.address,
                    address.postalCode,
                    address.user
                ));
            });
            resident.groups.forEach((item) => {
                groups.push({id: item.group.id, itemName: item.group.name});
            });

            const newResident = new Resident();
            newResident.id = resident.id;
            newResident.suiteUserId = suiteUser.id;
            newResident.isValidEmail = resident.isValidEmail;
            if (resident.email) newResident.email = resident.email;
            if (resident.alternativeEmails) newResident.alternativeEmails = resident.alternativeEmails;
            if (resident.photo) newResident.photo = resident.photo;
            if (resident.fullName) newResident.fullName = resident.fullName;
            if (suiteUser.occupancyDate) newResident.occupancyDate = suiteUser.occupancyDate;
            if (suiteUser.moveOutDate) newResident.moveOutDate = suiteUser.moveOutDate;
            if (resident.dateOfBirth) newResident.dateOfBirth = resident.dateOfBirth;
            if (addressesArr.length) newResident.addresses = addressesArr;
            if (phonesArr.length) newResident.phones = phonesArr;
            newResident.groups = groups;
            if (resident.emergencyContact) newResident.emergencyContact = resident.emergencyContact;
            if (resident.emergencyContacts) newResident.emergencyContacts = resident.emergencyContacts;
            if (resident.emergencyAssistantNotes) newResident.emergencyAssistantNotes = resident.emergencyAssistantNotes;
            if (resident.note) newResident.note = resident.note;
            if (suiteUser.role) newResident.role = suiteUser.role;
            if (resident.emailWaiverSigned) newResident.emailWaiverSigned = resident.emailWaiverSigned ? 1 : 0;
            if (resident.parcelWaiverSigned) newResident.parcelWaiverSigned = resident.parcelWaiverSigned ? 1 : 0;
            if (resident.keyWaiverSigned) newResident.keyWaiverSigned = resident.keyWaiverSigned ? 1 : 0;
            if (resident.maxTvUser) newResident.maxTvUser = resident.maxTvUser ? 1 : 0;
            if (resident.residentStatus) newResident.residentStatus = resident.residentStatus;
            if (resident.isInvited) newResident.isInvited = resident.isInvited ? 1 : 0;
            if (resident.oauthId) newResident.oauthId = resident.oauthId;
            if (resident.ownerInformationFormReceived) newResident.ownerInformationFormReceived = resident.ownerInformationFormReceived;
            if (resident.leaseReceived) newResident.leaseReceived = resident.leaseReceived;
            if (resident.tenantInformationFormReceived) newResident.tenantInformationFormReceived = resident.tenantInformationFormReceived;
            if (resident.primaryLeaseHolder) newResident.primaryLeaseHolder = resident.primaryLeaseHolder;
            if (resident.leaseStartDate) newResident.leaseStartDate = resident.leaseStartDate;
            if (resident.disabilities) newResident.disabilities = resident.disabilities;
            if (resident.consent) newResident.consent = resident.consent;
            if (resident.buzzFields) newResident.buzzFields = resident.buzzFields;
            if (resident.keyCodeFields) newResident.keyCodeFields = resident.keyCodeFields;
            if (resident.keyFobFields) newResident.keyFobFields = resident.keyFobFields;
            if (resident.vehicles) newResident.vehicles = resident.vehicles;

            if (suiteUser.suite) newResident.suiteId = suiteUser.suite.id;
            if (suiteUser.suite) newResident.suiteNumber = suiteUser.suite.suiteNumber;
            if (suiteUser.suite && suiteUser.suite.buzzFields) newResident.suiteBuzzFields = suiteUser.suite.buzzFields;
            if (suiteUser.suite && suiteUser.suite.keyCodeFields) newResident.suiteKeyCodeFields = suiteUser.suite.keyCodeFields;
            if (suiteUser.suite && suiteUser.suite.keyFobFields) newResident.suiteKeyFobFields = suiteUser.suite.keyFobFields;
            if (suiteUser.building) newResident.buildingId = suiteUser.building.id;
            if (suiteUser.building) newResident.buildingName = suiteUser.building.name;
            if (suiteUser.building) newResident.buildingAddress = suiteUser.building.address;
            if (suiteUser.building && suiteUser.building.timeZone) newResident.buildingTimezone = suiteUser.building.timeZone.value;
            if (suiteUser.building) newResident.corporationNumber = suiteUser.building.corporationNumber;
            if (suiteUser.building && suiteUser.building.cNSettings) newResident.cNSettings = suiteUser.building.cNSettings;
            if (suiteUser.suite && suiteUser.suite.pet) newResident.pets = suiteUser.suite.pet;
            if (suiteUser.suite && suiteUser.suite.commonElementsFee) newResident.commonElementsFee = suiteUser.suite.commonElementsFee;
            newResident.isCanEdit = suiteUser.isCanEdit;
            newResident.isCanDelete = suiteUser.isCanDelete;

            const selItem = this.selectedItemData;
            if (selItem && selItem.id == suiteUser.user.id) newResident.checked = true;
            return newResident;
        });
        return residents;
    }

    openPdfInNewTab(pathToPdf) {
        window.open(pathToPdf);
    }

    bookAmenityForResident(data) {
        const url = `/v1/amenity?expand=buildingAmenity,fees,events,events.bookings&enable_status=1&per-page=1000&building_id=${data.buildingId}`;
        this.api.get('domainAmenities', `${url}`).subscribe(
            (res) => {
                const body = JSON.parse(res._body);
                if (body.entities.length > 0) {
                    this.amenitiesService.amenityInAmenityModal = new Amenity();

                    this.amenitiesService.amenityInAmenityModal.buildingId = data.buildingId;
                    this.amenitiesService.amenityInAmenityModal.buildingName = data.buildingName;
                    this.amenitiesService.amenityInAmenityModal.buildingTimezone = data.buildingTimezone;
                    this.amenitiesService.amenityInAmenityModal.suiteId = data.suiteId;
                    this.amenitiesService.amenityInAmenityModal.suiteNumber = data.suiteNumber;
                    this.amenitiesService.amenityInAmenityModal.residentId = data.id;
                    this.amenitiesService.amenityInAmenityModal.residentFullName = data.fullName;
                    this.amenitiesService.amenityInAmenityModal.residentEmail = data.email;

                    this.amenitiesService.isPrefilledFromDirectory = true;
                    this.amenitiesService.isAmenityModalShow = true;
                } else
                    toastr.error('No Amenities exist in this building.');
            },
            (error) => this.api.errorHandler(error, 'Failed to get Amenity List For This Building')
        );
    }

    createParkingPassForResident(data) {
        const url = `/v1/parking_pass_buildings.json?per-page=1000&order-by=buildingName|asc&expand=parkingLots`;
        this.api.get('domainParkingPass', url).subscribe(
            (res) => {
                this.parkingPassService.buildings = JSON.parse(res._body).entities.map(building => ({
                    id: building.buildingId,
                    name: building.buildingName,
                    timezone: building.timeZone,
                    parkingLots: building.parkingLots
                }));
                this.parkingPassService.getFilteredBuildings();
                if (this.parkingPassService.buildings.find(b => b.id == +data.buildingId)) {

                    this.parkingPassService.parkingPassInParkingPassModal = new ParkingPass();

                    this.parkingPassService.parkingPassInParkingPassModal.buildingId = +data.buildingId;
                    this.parkingPassService.parkingPassInParkingPassModal.buildingName = data.buildingName;
                    this.parkingPassService.parkingPassInParkingPassModal.suiteId = data.suiteId;
                    this.parkingPassService.parkingPassInParkingPassModal.suiteNumber = data.suiteNumber;
                    this.parkingPassService.parkingPassInParkingPassModal.residentId = data.id;
                    this.parkingPassService.parkingPassInParkingPassModal.residentFullName = data.fullName;
                    this.parkingPassService.parkingPassInParkingPassModal.residentEmail = data.email;

                    this.parkingPassService.isPrefilledFromDirectory = true;
                    this.parkingPassService.isParkingPassModalShow = true;
                } else
                    toastr.error('No Parking exist in this building');
            },
            (error) => this.api.errorHandler(error, 'Failed to get buildings with parkings.')
        );
    }

    getResidentInstructions(id) {
        this.selectedResidentInstructions = [];
        const url = `/v1/resident_instruction?user_id=${id}`;
        this.instructionsObservable = this.api.get('domainBD', `${url}`).map((response) => response.json())
            .subscribe(
                (res) => {
                    this.selectedResidentInstructions = [];
                    this.selectedResidentInstructions = res.entities;
                    this.selectedResidentInstructions.forEach(i => {
                        i.startDate = i.startDate.slice(0, -6);
                        i.endDate = i.endDate ? i.endDate.slice(0, -6) : '';
                        if (moment(i.startDate)._d > moment()._d)
                            i.status = 'future';
                        else if (moment(i.endDate)._d < moment()._d)
                            i.status = 'past';
                        else if (moment()._d >= moment(i.startDate)._d && moment()._d <= moment(i.endDate)._d) {
                            i.status = 'current';
                            i.visible = true;
                        } else if (moment()._d >= moment(i.startDate)._d && !i.endDate) {
                            i.status = 'current';
                            i.visible = true;
                        }
                    });
                }, (error) => this.api.errorHandler(error, 'Failed to get resident instructions.')
            );
    }

    deleteResidentInstruction(instruction) {
        const url = `/v1/resident_instruction/${instruction.id}.json`;
        this.api.remove('domainBD', url).subscribe(
            (res) => {
                toastr.success('Instruction has been deleted successfully.');
                const index = this.selectedResidentInstructions.findIndex(i => i.id == instruction.id);
                this.selectedResidentInstructions.splice(index, 1);
            },
            (error) => this.api.errorHandler(error, 'Failed to update resident instructions.')
        );
    }

    deleteInstructionFromUnit(instruction) {
        const url = `/v1/resident_instruction/${instruction.id}.json`;
        this.api.remove('domainBD', url).subscribe(
            (res) => {
                toastr.success('Instruction has been deleted successfully.');
                const index = this.selectedItemData.instructions.findIndex(i => i.id == instruction.id);
                this.selectedItemData.instructions.splice(index, 1);
            },
            (error) => this.api.errorHandler(error, 'Failed to update resident instructions.')
        );
    }

    formatResidentInstructions(instructions) {
        instructions.forEach(i => {
            i.startDate = i.startDate.slice(0, -6);
            i.endDate = i.endDate ? i.endDate.slice(0, -6) : '';
            if (moment(i.startDate)._d > moment()._d)
                i.status = 'future';
            else if (moment(i.endDate)._d < moment()._d)
                i.status = 'past';
            else if (moment()._d >= moment(i.startDate)._d && moment()._d <= moment(i.endDate)._d)
                i.status = 'current';
            else if (moment()._d >= moment(i.startDate)._d && !i.endDate)
                i.status = 'current';
        });
    }

    editInstructionFromUnitView(instruction) {
        this.instructionsModalUser = instruction.user.id;
        this.showAddInstructionsModal = true;
        this.instructionsModalType = instruction.type;
        this.instructionInInstructionsModal = instruction;
    }

    dateIsLaterThanCurrent(date) {
        return date && new Date(date) > new Date();
    }

    dateIsEarlierThanCurrent(date) {
        return date && new Date(date) < new Date();
    }

    getVendorsCategories() {
        const url = '/v1/admin/categories?per-page=1000';
        this.api.get('domainVendors', url).subscribe(
            (res) => {
                this.vendorsCategories = [];
                const body = JSON.parse(res._body);
                this.vendorsCategories = body.entities || [];
                this.vendorsCategories = this.vendorsCategories.map(c => ({...c, itemName: c.name}));
                this.vendorsCategoriesReceived.emit();
            },
            (error) => this.api.errorHandler(error, 'Failed to get vendors categories.')
        );
    }

    getVendorsPaymentMethods() {
        const url = '/v1/admin/payment_methods?per-page=1000';
        this.api.get('domainVendors', url).subscribe(
            (res) => {
                this.vendorsPaymentMethods = [];
                const body = JSON.parse(res._body);
                this.vendorsPaymentMethods = body.entities || [];
                this.vendorsPaymentMethods = this.vendorsPaymentMethods.map(c => ({...c, itemName: c.name}));
            },
            (error) => this.api.errorHandler(error, 'Failed to get vendors categories.')
        );
    }

    userIsSuperAdmin() {
        const userType = localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).userType : '';
        return userType == 'super_admin';
    }

    archivedFilterIsOn() {
        return this.urlService.queryParams.show_moved_out ? true : false;
    }

    getOccupancyTypes() {
        this.occupancyTypesLoading = true;
        const url = '/v1/all_occupancy_types.json?per-page=10000';
        this.api.getJson('domainBD', url).subscribe(
            result => {
                this.occupancyTypesLoading = false;
                this.residentOccupancyTypes = result.entities || [];
            }, error => {
                this.occupancyTypesLoading = false;
                this.api.errorHandler(error, 'Failed to get occupancy types!');
            }
        );
    }

    restoreResident(suite, email, view?) {
        const url = `/v1/suite-users.json?expand=user&suite_id=${suite}&user_email=${encodeURIComponent(email)}&show_moved_out=true`;
        this.api.getJson('domainBD', url).subscribe(
            result => {
                if (result.entities.length > 0) {
                    const resident = result.entities[0];
                    const errorText = `This email address can’t be used for resident. It belongs to another resident (${resident.user.fullName}, ${resident.user.email}${this.getPrimaryPhone(resident.user.phones)})`;
                    const popupConfig = {
                        title: 'Restore resident?',
                        text: errorText,
                        type: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#DD6B55',
                        confirmButtonText: 'Restore',
                        cancelButtonText: 'No',
                        closeOnConfirm: true
                    };

                    swal(popupConfig,
                        () => {
                            const url = `/v1/suite-users/${resident.id}`;
                            const data = {moveOutDate: null};
                            this.api.patch('domainBD', url, data).subscribe(
                                res => {
                                    toastr.success('Resident was restored successfully.');
                                    if (view == 'detailed')
                                        this.getData();
                                }, error => this.api.errorHandler(error, 'Failed to restore Resident.')
                            );
                        }
                    );
                }
            }
        );
    }

    getPrimaryPhone(phones) {
        if (phones.length > 0) {
            const primary = phones.find(p => p.isPrimaryPhone);
            return ', ' + (primary ? primary.phone : phones[0].phone);
        } else
            return '';
    }

    getCorporations(): void {
        this.loadingCorporations = true;
        const url = `/v1/corporation_number.json?per-page=10000&order-by=name|asc`;
        this.api.get('domainBD', url).subscribe(
            res => {
                this.loadingCorporations = false;
                this.corporations = JSON.parse(res._body).entities || [];
                this.corporations = this.corporations.map(c => ({
                    ...c,
                    id: +c.id,
                    itemName: c.name
                }));
            },
            error => this.api.errorHandler(error, 'Failed get buildings')
        );
    }

    getCorporationSettings(id) {
        this.currentCorporationSettings = {};
        if (this.corporationSettingsSubscribtion)
            this.corporationSettingsSubscribtion.unsubscribe();
        const url = `/v1/corporation_number/${id}.json?expand=settings`;
        this.corporationSettingsSubscribtion = this.api.get('domainBD', url).subscribe(
            (res) => {
                const body = JSON.parse(res._body);
                this.currentCorporationSettings = body.settings;
            },
            error => this.api.errorHandler(error, 'Failed get Services')
        );
    }

    getCorporationSmsSettings(id) {
        this.currentCorporationSmsSettings = {};
        if (this.corporationSettingsSmsSubscribtion)
            this.corporationSettingsSmsSubscribtion.unsubscribe();
        const url = `/v1/cn_settings/${id}.json`;
        this.corporationSettingsSmsSubscribtion = this.api.get('domainSms', url).subscribe(
            (res) => this.currentCorporationSmsSettings = JSON.parse(res._body),
            error => this.api.errorHandler(error, 'Failed to get Sms Settings')
        );
    }

    onImageCaptureModalClose(eventData) {
        this.showImageCaptureModal = false;
        if (eventData)
            this.imageCaptured.emit(eventData);
    }

    sendSms(data) {
        this.showSmsModal = true;
        this.smsMessageResidentData = {
            corporationNumberId: +data.corporationNumber || +data.suiteUser[0].building.corporationNumber,
            suite: {
                bdId: data.suiteId || data.suiteUser[0].suite.id,
                suiteNumber: data.suiteNumber || data.suiteUser[0].suite.suiteNumber
            },
            resident: {
                bdId: data.id.toString(),
                fullName: data.fullName
            },
            cellPhones: this.getCellPhones(data.phones),
            senderPhone: this.currentCorporationSmsSettings.twilioSettings.activePhones[0]
        };
    }

    getCellPhones(phones) {
        return phones.filter(p => p.phoneType == 'phone_cell' && p.realPhoneType == 'phone_cell');
    }

    showSendSms() {
        let show = false;

        if (this.currentCorporationSmsSettings) {
            show = this.currentCorporationSmsSettings.isEnable &&
                this.currentCorporationSmsSettings.twilioSettings.sid &&
                this.currentCorporationSmsSettings.twilioSettings.friendlyName;
        }

        return show;
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/directory.service.ts